import * as React from 'react'
import { styled } from 'next-yak'
import { Project } from '@owl-nest/api-client/latest'

import * as api from '@owl-nest/api-client/next'
import * as shadow from '@owl-nest/shadow'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'
import * as sharing from '@owl-nest/sharing'
import * as copier from '@owl-nest/copier'

type SharingLinksProps = {
  enableWidget?: boolean
  project: Project
  user?: api.PublicUser
  sharingContext: string
  sharingExtraData: { eventCategory: string; eventLabel: string }
  widget?: React.ReactNode
}

export function SharingLinks({
  enableWidget = true,
  project,
  user,
  sharingContext,
  sharingExtraData,
  widget = <></>,
}: SharingLinksProps): React.ReactElement<any> {
  const { canCopy, copy, copyStatus } = copier.useCopy(4000)
  const { shareTo } = sharing.useSharing(sharingContext, project, user)
  const tracking = shadow.useTracking()

  const projectURL = sharing.getSharingURL(sharingContext, 'clipboard', project, user)

  return (
    <>
      {enableWidget && <CopySection displayProjectURL={true} />}

      <Links $enableWidget={enableWidget}>
        <plume.Link
          withIcon
          onClick={() => {
            shareTo('linkedin', sharingExtraData)
          }}
        >
          <plume.glyphs.thirdParty.Linkedin size={16} />
          LinkedIn
        </plume.Link>

        <plume.Link withIcon onClick={() => shareTo('whatsapp', sharingExtraData)}>
          <plume.glyphs.thirdParty.WhatsApp size={16} />
          WhatsApp
        </plume.Link>

        <plume.Link withIcon onClick={() => shareTo('facebook', sharingExtraData)}>
          <plume.glyphs.thirdParty.Facebook size={16} />
          Facebook
        </plume.Link>

        <plume.Link withIcon onClick={() => shareTo('messenger', sharingExtraData)}>
          <plume.glyphs.thirdParty.Messenger size={16} />
          Messenger
        </plume.Link>

        <plume.Link withIcon onClick={() => shareTo('twitter', sharingExtraData)}>
          <plume.glyphs.thirdParty.Twitter size={16} />X
        </plume.Link>

        {enableWidget && widget}
      </Links>

      {!enableWidget && <CopySection displayProjectURL={false} />}
    </>
  )

  function CopySection({ displayProjectURL }: { displayProjectURL: boolean }): React.ReactElement<any> {
    return (
      <Copy>
        {displayProjectURL && <plume.TextField value={projectURL} disabled />}
        <plume.LinkAsButton
          type="button"
          tinted
          withIcon
          onClick={() => {
            if (canCopy) {
              copy(projectURL)
              track(shadow.SOCIAL_NETWORK.HYPERLINK)
            }
          }}
        >
          <plume.glyphs.stroke.Link size={20} />
          {t('Copy link')}
        </plume.LinkAsButton>

        <plume.Tooltip open={copyStatus === 'succeeded'} position={'bottom'} fitToContent>
          <plume.styles.copy.S.regular>{t('Link copied to your clipboard.')}</plume.styles.copy.S.regular>
        </plume.Tooltip>
      </Copy>
    )
  }

  function track(kind: shadow.SOCIAL_NETWORK): void {
    tracking.social.track({
      eventCategory: shadow.CATEGORY.CHECKOUT,
      eventLabel: sharingExtraData.eventLabel,
      socialAction: 'click: Share',
      socialNetwork: kind,
      socialTarget: projectURL,
    })
  }
}

const Links = styled.div<{
  $enableWidget: boolean
}>`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 24px 16px;
  justify-content: space-evenly;
  max-width: ${({ $enableWidget }) => ($enableWidget ? '272px' : '100%')};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: ${({ $enableWidget }) => ($enableWidget ? 'grid' : 'flex')};
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 16px;
    max-width: 100%;
  }

  ${plume.Link} {
    ${plume.glyphs.Icon} {
      margin-right: 5px;
    }
  }

  ${plume.glyphs.thirdParty.Linkedin} {
    color: ${plume.COLORS.TERTIARY_LINKEDIN};
  }

  ${plume.glyphs.thirdParty.WhatsApp} {
    color: ${plume.COLORS.TERTIARY_WHATSAPP};
  }

  ${plume.glyphs.thirdParty.Facebook} {
    color: ${plume.COLORS.TERTIARY_FACEBOOK};
  }

  ${plume.glyphs.thirdParty.Messenger} {
    color: ${plume.COLORS.TERTIARY_MESSENGER};
  }

  ${plume.glyphs.thirdParty.Twitter} {
    color: ${plume.COLORS.TERTIARY_X};
  }

  ${plume.glyphs.stroke.CodeBlock} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const Copy = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 34px;
  position: relative;
  flex-direction: column;
  width: 100%;

  ${plume.glyphs.stroke.Link} {
    color: ${({ theme }) => (theme as any)?.colors?.primary ?? plume.COLORS.PRIMARY_BLUE_500};
  }

  ${plume.TextField} {
    width: 100%;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    min-width: 470px;
    justify-content: center;
    flex-direction: row;
    width: auto;

    ${plume.TextField} {
      min-width: 350px;
      width: auto;
    }

    ${plume.Tooltip} {
      right: -180px;
      left: auto;

      ${plume.styles.copy.S.regular} {
        text-align: center;
      }
    }
  }
`
